import { CheckCircle2, KeyRound, Loader2, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { ErrorState } from '@/ui/ErrorState';

import { useDefinirSenha } from './api';

/**
 * Destino do link do e-mail `PRE_CREDENCIADO_ACESSO` (F09, D-19) —
 * `/credenciados/pre-credenciado/acesso#<token>`.
 *
 * O token vem no fragmento (`#`), que o navegador não manda a servidor
 * nenhum, e sai da barra de endereço assim que é lido. Nada acontece ao abrir
 * a página: leitor de e-mail que abre links sozinho não gasta o link de uso
 * único. O botão pede à API que o Keycloak mande o e-mail com o link de
 * criação de senha — a senha é digitada só na página do Keycloak
 * (docs/AUTENTICACAO-E-ACESSO.md §7); esta tela não tem campo de senha.
 */

const FORMATO_DO_TOKEN = /^[0-9a-f]{64}$/;

function lerTokenDoFragmento(): string | null {
  const bruto = window.location.hash.replace(/^#/, '').trim().toLowerCase();
  return FORMATO_DO_TOKEN.test(bruto) ? bruto : null;
}

export function AcessoPreCredenciadoPage() {
  const [token] = useState(lerTokenDoFragmento);
  const pedido = useDefinirSenha();

  useEffect(() => {
    document.title = 'Acesso do pré-credenciado — Sistema TEG';
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const moldura = (conteudo: React.ReactNode) => (
    <section className="mx-auto w-full max-w-xl px-4 py-10 sm:px-6">{conteudo}</section>
  );

  if (!token) {
    return moldura(
      <ErrorState
        variant="resource-not-found"
        title="Este link está incompleto"
        description="Abra o endereço exatamente como chegou no e-mail. Se já criou a senha, entre pelo Portal do Credenciado."
        primaryAction={{ label: 'Entrar no Portal do Credenciado', to: '/login' }}
      />,
    );
  }

  const resultado = pedido.data?.resultado;

  if (resultado === 'ENVIADO') {
    return moldura(
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MailCheck className="text-success h-5 w-5" aria-hidden />
            Confira o seu e-mail
          </CardTitle>
          <CardDescription>
            Enviamos para o e-mail da sua inscrição o link para criar a senha. Ele vale por 12 horas
            e abre a página segura de identidade do Sistema TEG, onde a senha é criada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>Depois de criar a senha, entre no Portal do Credenciado com o seu e-mail.</p>
          <Button asChild className="h-11 w-full sm:h-10 sm:w-auto">
            <Link to="/login">Entrar no Portal do Credenciado</Link>
          </Button>
        </CardContent>
      </Card>,
    );
  }

  if (resultado === 'SENHA_JA_DEFINIDA') {
    return moldura(
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="text-success h-5 w-5" aria-hidden />
            Sua senha já está criada
          </CardTitle>
          <CardDescription>
            Entre no Portal do Credenciado com o seu e-mail e a sua senha.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="h-11 w-full sm:h-10 sm:w-auto">
            <Link to="/login">Entrar no Portal do Credenciado</Link>
          </Button>
        </CardContent>
      </Card>,
    );
  }

  if (resultado === 'LINK_INVALIDO') {
    return moldura(
      <ErrorState
        variant="resource-not-found"
        title="Este link já foi usado ou expirou"
        description="Se você ainda não criou a senha, peça um novo link em “Esqueci minha senha”, com o e-mail da sua inscrição."
        primaryAction={{ label: 'Esqueci minha senha', to: '/esqueci-minha-senha' }}
        secondaryAction={{ label: 'Entrar no Portal do Credenciado', to: '/login' }}
      />,
    );
  }

  const indisponivel = resultado === 'INDISPONIVEL' || pedido.isError;
  const muitasTentativas = pedido.error?.situacao === 429;

  return moldura(
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <KeyRound className="h-5 w-5" aria-hidden />
          Criar a senha do Portal do Credenciado
        </CardTitle>
        <CardDescription>
          Sua inscrição foi pré-credenciada e a sua conta no Portal do Credenciado já existe. Para
          criar a senha, peça o link seguro abaixo: ele chega no e-mail da sua inscrição.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {indisponivel && (
          <p role="alert" className="text-destructive text-sm">
            {muitasTentativas
              ? 'Muitas tentativas seguidas. Espere alguns minutos e tente de novo.'
              : 'Não foi possível pedir o link agora. Tente de novo em alguns minutos.'}
          </p>
        )}
        <Button
          className="h-11 w-full sm:h-10 sm:w-auto"
          disabled={pedido.isPending}
          onClick={() => pedido.mutate(token)}
        >
          {pedido.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Receber o link para criar a senha
        </Button>
      </CardContent>
    </Card>,
  );
}

export default AcessoPreCredenciadoPage;
